import { useEffect, useMemo, useRef, useState } from "react";
import { Activity, ArrowDownUp, RotateCw, Search, SlidersHorizontal } from "lucide-react";
import { fetchActivity } from "../api/activityLog";
import type { ActivityEntry } from "../api/activityLog";
import { Badge } from "../components/Badge";
import type { BadgeVariant } from "../components/Badge";
import { FilterPopover } from "../components/FilterPopover";
import type { FilterOptionGroup } from "../components/FilterPopover";
import { relativeTime } from "../utils/time";

/** Reuses Badge's existing color variants rather than inventing new CSS —
 *  green for "this created/finished something good", gold for "in
 *  progress/changed", red for "removed", neutral for plain auth events. */
function eventVariant(event: string): BadgeVariant {
  const v = event.toLowerCase();
  if (v === "created" || v === "approved" || v === "saved") return "status-approved";
  if (v === "deleted") return "status-blocked";
  if (v === "updated" || v === "statuschanged") return "status-pilot";
  return "outline";
}

type FilterKey = "category" | "event" | "user";

function buildGroup(
  key: FilterKey,
  label: string,
  entries: ActivityEntry[],
  pick: (e: ActivityEntry) => string
): FilterOptionGroup {
  const counts = new Map<string, number>();
  for (const e of entries) {
    const v = pick(e).trim();
    if (!v) continue;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return {
    key,
    label,
    options: Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => a.value.localeCompare(b.value)),
  };
}

export function ActivityPage() {
  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<Record<FilterKey, string[]>>({
    category: [],
    event: [],
    user: [],
  });
  const [sortAsc, setSortAsc] = useState(false);
  const [openFilter, setOpenFilter] = useState<FilterKey | null>(null);

  const triggerRefs = {
    category: useRef<HTMLButtonElement>(null),
    event: useRef<HTMLButtonElement>(null),
    user: useRef<HTMLButtonElement>(null),
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchActivity();
      setEntries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const groups: Record<FilterKey, FilterOptionGroup> = {
    category: buildGroup("category", "Category", entries, (e) => e.category),
    event: buildGroup("event", "Event", entries, (e) => e.event),
    user: buildGroup("user", "User", entries, (e) => e.userName),
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return entries.filter((e) => {
      if (filters.category.length && !filters.category.includes(e.category)) return false;
      if (filters.event.length && !filters.event.includes(e.event)) return false;
      if (filters.user.length && !filters.user.includes(e.userName)) return false;
      if (!q) return true;
      const haystack =
        `${e.userName} ${e.userEmail} ${e.category} ${e.event} ${e.entityKind} ${e.entityId} ${e.description}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [entries, filters, query]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) =>
      sortAsc ? a.timestamp.localeCompare(b.timestamp) : b.timestamp.localeCompare(a.timestamp)
    );
    return copy;
  }, [filtered, sortAsc]);

  const activeFilterCount = Object.values(filters).reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="page activity-page">
      <div className="page-hero">
        <p className="page-subtitle">
          <Activity size={15} aria-hidden style={{ verticalAlign: "-2px", marginRight: 6 }} />
          Sign-ins and actions across Initiatives, Platforms, and Library — admin only.
        </p>
      </div>

      <div className="admin-table-wrap">
        <div className="admin-toolbar">
          <div className="admin-search">
            <Search size={14} aria-hidden />
            <input
              type="search"
              placeholder={`Search ${entries.length} activity entries…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search activity"
            />
          </div>

          {(["category", "event", "user"] as FilterKey[]).map((key) => (
            <button
              key={key}
              ref={triggerRefs[key]}
              type="button"
              className={`filter-trigger${filters[key].length > 0 ? " has-filters" : ""}`}
              onClick={() => setOpenFilter((o) => (o === key ? null : key))}
              aria-expanded={openFilter === key}
              aria-haspopup="dialog"
            >
              <SlidersHorizontal size={14} />
              <span>{groups[key].label}</span>
              {filters[key].length > 0 ? (
                <span className="filter-trigger-count">{filters[key].length}</span>
              ) : null}
            </button>
          ))}

          <button
            type="button"
            className="btn btn-ghost activity-sort-btn"
            onClick={() => setSortAsc((a) => !a)}
          >
            <ArrowDownUp size={14} />
            Sort: {sortAsc ? "Oldest first" : "Newest first"}
          </button>

          <button
            type="button"
            className="btn btn-ghost"
            onClick={load}
            disabled={loading}
            aria-label="Refresh"
            title="Refresh"
          >
            <RotateCw size={15} className={loading ? "add-item-spin" : undefined} />
          </button>
        </div>

        {(["category", "event", "user"] as FilterKey[]).map((key) => (
          <FilterPopover
            key={key}
            open={openFilter === key}
            anchor={triggerRefs[key].current}
            groups={[groups[key]]}
            initialValues={{ [key]: filters[key] }}
            onApply={(next) =>
              setFilters((f) => ({ ...f, [key]: next[key] ?? [] }))
            }
            onClose={() => setOpenFilter(null)}
          />
        ))}

        {error ? (
          <div className="error-banner" role="alert">
            <strong>Couldn't load activity.</strong> {error}
          </div>
        ) : null}

        <div className="admin-table-scroll">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>User</th>
                <th>Category</th>
                <th>Event</th>
                <th>Entity</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="activity-empty">
                    Loading…
                  </td>
                </tr>
              ) : sorted.length === 0 ? (
                <tr>
                  <td colSpan={6} className="activity-empty">
                    {activeFilterCount > 0 || query
                      ? "No activity matches your filters."
                      : "No activity recorded yet."}
                  </td>
                </tr>
              ) : (
                sorted.map((e, i) => (
                  <tr key={`${e.timestamp}-${i}`}>
                    <td className="admin-td-time">{relativeTime(e.timestamp) || "—"}</td>
                    <td>{e.userName || "Unknown"}</td>
                    <td className="admin-td-text">{e.category}</td>
                    <td>
                      <Badge variant={eventVariant(e.event)}>{e.event}</Badge>
                    </td>
                    <td className="admin-td-text">
                      {e.entityKind ? `${e.entityKind} ${e.entityId}`.trim() : "—"}
                    </td>
                    <td className="admin-td-text">{e.description}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
